import { FormControlLabel, FormControlLabelProps, Radio, RadioGroup, RadioGroupProps } from '@mui/material';
import { FieldValues, FieldPath, UseControllerProps, useController } from 'react-hook-form';

import type { ReactNode } from 'react';

export type RadioGroupInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> = UseControllerProps<TFieldValues, TName> &
  RadioGroupProps & {
    FormControlLabelProps?: Omit<FormControlLabelProps, 'control' | 'label' | 'value'>;
    options: Array<{
      value: string;
      label: ReactNode;
    }>;
    transformValue?: (value: string) => TFieldValues[TName];
  };

export const RadioGroupInput = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  name,
  control,
  rules,
  defaultValue,
  options,
  onChange,
  transformValue = value => value as TFieldValues[TName], // Usefull for transforming the value to a number or boolean
  FormControlLabelProps,
  ...RadioGroupProps
}: RadioGroupInputProps<TFieldValues, TName>) => {
  const { field } = useController({
    name,
    control,
    rules,
    defaultValue
  });

  return (
    <RadioGroup
      {...field}
      onChange={(event, value) => {
        // @ts-ignore: Temporary ignore https://github.com/react-hook-form/react-hook-form/issues/10669
        field.onChange(transformValue(value));
        onChange?.(event, value);
      }}
      {...RadioGroupProps}
    >
      {options.map(({ value, label }) => (
        <FormControlLabel key={value} value={value} control={<Radio />} label={label} {...FormControlLabelProps} />
      ))}
    </RadioGroup>
  );
};
